/**
 * Health — checkups list + create + complete + due-soon dashboard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const TYPES = ["annual", "vaccination", "dental", "vision", "custom"];

function CompleteDialog({ checkup, onClose, onDone }) {
  const [vitals, setVitals] = useState({ height_cm: "", weight_kg: "", bp_systolic: "", bp_diastolic: "", hb: "", notes: "" });
  const [notes, setNotes] = useState("");
  if (!checkup) return null;
  const num = (v) => (v === "" ? null : Number(v));
  const submit = async () => {
    try {
      await api.post(`/health/checkups/${checkup.id}/complete`, {
        vitals: {
          height_cm: num(vitals.height_cm),
          weight_kg: num(vitals.weight_kg),
          bp_systolic: num(vitals.bp_systolic),
          bp_diastolic: num(vitals.bp_diastolic),
          hb: num(vitals.hb),
          notes: vitals.notes || null,
        },
        nurse_notes: notes,
      });
      toast.success("Checkup completed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="health-complete-dialog">
      <div className="bg-white rounded-lg p-5 max-w-md w-full space-y-3">
        <div className="font-medium">Complete checkup</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Height (cm)</Label><Input data-testid="hc-h" type="number" step="0.1" value={vitals.height_cm} onChange={(e) => setVitals({ ...vitals, height_cm: e.target.value })} className="bg-white font-mono" /></div>
          <div><Label>Weight (kg)</Label><Input data-testid="hc-w" type="number" step="0.1" value={vitals.weight_kg} onChange={(e) => setVitals({ ...vitals, weight_kg: e.target.value })} className="bg-white font-mono" /></div>
          <div><Label>BP systolic</Label><Input data-testid="hc-bps" type="number" value={vitals.bp_systolic} onChange={(e) => setVitals({ ...vitals, bp_systolic: e.target.value })} className="bg-white font-mono" /></div>
          <div><Label>BP diastolic</Label><Input data-testid="hc-bpd" type="number" value={vitals.bp_diastolic} onChange={(e) => setVitals({ ...vitals, bp_diastolic: e.target.value })} className="bg-white font-mono" /></div>
          <div><Label>Hb (g/dL)</Label><Input data-testid="hc-hb" type="number" step="0.1" value={vitals.hb} onChange={(e) => setVitals({ ...vitals, hb: e.target.value })} className="bg-white font-mono" /></div>
        </div>
        <Textarea data-testid="hc-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Nurse notes" className="bg-white" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="hc-submit" onClick={submit}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function CheckupsList({ pending }) {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [draft, setDraft] = useState({ student_id: "", type: "annual", due_date: "", custom_label: "" });
  const [completeing, setCompleteing] = useState(null);

  const load = () => api.get("/health/checkups", { params: { only_pending: pending } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/students").then((r) => setStudents(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const create = async () => {
    if (!draft.student_id) { toast.error("Pick a student"); return; }
    if (!draft.due_date) { toast.error("Pick a due date"); return; }
    try {
      await api.post("/health/checkups", draft);
      toast.success("Checkup scheduled");
      setDraft({ student_id: "", type: "annual", due_date: "", custom_label: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="vs-card p-4 space-y-3">
        <div className="eyebrow">Schedule new checkup</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Select value={draft.student_id} onValueChange={(v) => setDraft({ ...draft, student_id: v })}>
            <SelectTrigger data-testid="hc-student" className="bg-white"><SelectValue placeholder="Student" /></SelectTrigger>
            <SelectContent>{students.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.vs_id}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
            <SelectTrigger data-testid="hc-type" className="bg-white capitalize"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => (<SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>))}</SelectContent>
          </Select>
          <Input data-testid="hc-due" type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} className="bg-white" />
          <Input data-testid="hc-label" placeholder="Custom label" value={draft.custom_label} onChange={(e) => setDraft({ ...draft, custom_label: e.target.value })} className="bg-white" />
          <Button data-testid="hc-create" onClick={create}>Schedule</Button>
        </div>
      </div>

      <div className="vs-card divide-y" data-testid="hc-list">
        {rows.map((r) => (
          <div key={r.id} className="p-3 flex items-center gap-3" data-testid={`hc-row-${r.id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm capitalize">{r.custom_label || r.type}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">due {r.due_date} · {r.student_id.slice(0, 8)}…</div>
              {r.reminder_sent_at && <div className="text-[10px] text-emerald-700">Reminder sent {r.reminder_sent_at.slice(0, 10)}</div>}
            </div>
            {r.completed_at ? (
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Completed</span>
            ) : (
              <Button size="sm" data-testid={`hc-complete-${r.id}`} onClick={() => setCompleteing(r)}>Complete</Button>
            )}
          </div>
        ))}
        {!rows.length && <div className="p-4 text-sm text-center text-[var(--vs-text-secondary)]">No checkups.</div>}
      </div>

      <CompleteDialog checkup={completeing} onClose={() => setCompleteing(null)} onDone={() => { setCompleteing(null); load(); }} />
    </div>
  );
}

function DueSoon() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/health/due-soon").then((r) => setRows(r.data)); }, []);
  const fire = async () => {
    try {
      const r = await api.post("/health/run-reminders");
      toast.success(`Sent ${r.data.sent} reminder(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-3">
      <Button data-testid="hc-run-reminders" onClick={fire}>Fire reminders for 3-days-out checkups</Button>
      <div className="vs-card divide-y">
        {rows.map((r) => (
          <div key={r.id} className="p-3 flex items-center justify-between" data-testid={`hc-soon-${r.id}`}>
            <div>
              <div className="font-medium text-sm capitalize">{r.custom_label || r.type}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">due {r.due_date}</div>
            </div>
            <span className="text-xs text-amber-700 font-mono">{r.due_date}</span>
          </div>
        ))}
        {!rows.length && <div className="p-4 text-sm text-center text-[var(--vs-text-secondary)]">No checkups due in the next 7 days.</div>}
      </div>
    </div>
  );
}

export default function Health() {
  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Health checkups</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Annual, vaccination, dental, vision &amp; custom checkup tracker. Reminders fire 3 days before due date.</p>
      </div>
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="due" data-testid="tab-due">Due soon</TabsTrigger>
        </TabsList>
        <TabsContent value="pending"><CheckupsList pending={true} /></TabsContent>
        <TabsContent value="all"><CheckupsList pending={false} /></TabsContent>
        <TabsContent value="due"><DueSoon /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
